import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { ImpersonationService } from '../services/impersonation.service';
import { CurrentUserStateService } from '../services/current-user-state.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let reqUrl = request.url.toLowerCase();

        // Handle single endpoint for authenticating via Azure AD
        if (reqUrl.includes('authentication/ssologin')) {
            return this.useAzureADToken(request, next);
        }

        // Handle single endpoint for authenticating via openid
        if (reqUrl.includes('authentication/openid/login')) {
            return this.useOpenIdToken(request, next);
        }
        // Handle all other endpoints
        return this.useLGToken(next, request);
    }

    useLGToken(next: HttpHandler, request: HttpRequest<any>) {
        if (this.currentUserStateService.currentUser && this.currentUserStateService.accessToken) {
            const impersonate = {
                loginEmail: this.impersonationService.isImpersonatingUser()
                    ? this.impersonationService.impersonateUser
                    : null,
                clientShortName: this.impersonationService.isImpersonatingClient()
                    ? this.impersonationService.impersonateClient
                    : null,
            };

            const impersonateB64 = btoa(unescape(encodeURIComponent(JSON.stringify(impersonate))));

            request = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${this.currentUserStateService.accessToken}`,
                    'LG-Impersonate': impersonateB64,
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                },
            });
        }

        return next.handle(request);
    }

    useAzureADToken(request: HttpRequest<any>, next: HttpHandler) {
        try {
            let msalToken = localStorage.getItem('msalAccessToken');
            let authHeader = `Bearer ${msalToken}`;

            let azureHeaderRequest = request.clone({
                setHeaders: {
                    Authorization: authHeader,
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                },
            });

            return next.handle(azureHeaderRequest);
        } catch (e) {
            return next.handle(request);
        }
    }

    useOpenIdToken(request: HttpRequest<any>, next: HttpHandler) {
        let token = localStorage.getItem('openid-token-storage');
        let configId = localStorage.getItem('openid-config-storage');

        // Remove these values from storage
        // When user clicks log out, we want them logged out of LG.
        // If we keep the values, they will automatically be logged back in to LG.
        localStorage.removeItem('openid-token-storage');
        localStorage.removeItem('openid-config-storage');

        if (!token) {
            return next.handle(request);
        }

        const authHeader = `Bearer ${token}`;
        return next.handle(
            request.clone({
                setHeaders: {
                    Authorization: authHeader,
                },
                setParams: {
                    token: token,
                    configId: configId,
                },
            })
        );
    }
}
